
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Activity } from "@/types";

export function useNotifications() {
  const [notifications, setNotifications] = useState<Activity[]>([]);
  
  const fetchNotifications = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data, error } = await supabase
      .from('activities')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(10);

    if (error) {
      console.error('Error fetching notifications:', error);
      return;
    }

    if (data) {
      setNotifications(data as Activity[]);
    }
  };

  const markAsRead = async (notificationId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase
        .from('activities')
        .update({ is_read: true })
        .eq('id', notificationId)
        .eq('user_id', user.id);

      if (error) {
        console.error('Error marking notification as read:', error);
        return;
      }

      // Fetch the updated notification to ensure we have the latest state
      const { data: updatedData, error: fetchError } = await supabase
        .from('activities')
        .select('*')
        .eq('id', notificationId)
        .single();

      if (fetchError) {
        console.error('Error fetching updated notification:', fetchError);
        return;
      }

      setNotifications(prev =>
        prev.map(notification =>
          notification.id === notificationId
            ? (updatedData as Activity)
            : notification
        )
      );
    } catch (error) {
      console.error('Error in markAsRead:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const unreadIds = notifications
        .filter(n => !n.is_read)
        .map(n => n.id);

      if (unreadIds.length === 0) return;

      const { error } = await supabase
        .from('activities')
        .update({ is_read: true })
        .eq('user_id', user.id)
        .in('id', unreadIds);

      if (error) {
        console.error('Error marking all as read:', error);
        return;
      }

      // Fetch all updated notifications to ensure we have the latest state
      await fetchNotifications();
    } catch (error) {
      console.error('Error in markAllAsRead:', error);
    }
  };

  useEffect(() => {
    fetchNotifications();

    const channel = supabase
      .channel('activities-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'activities',
          filter: `user_id=eq.${supabase.auth.getUser().then(({ data }) => data.user?.id)}`
        },
        (payload) => {
          console.log('Received realtime update:', payload);
          fetchNotifications();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  return {
    notifications,
    markAsRead,
    markAllAsRead,
    unreadCount: notifications.filter(n => !n.is_read).length
  };
}
